import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { api } from "@/lib/api";
import { tr } from "@/i18n/translate";

const COUNTS = [["translated", "translated"], ["skipped_human", "kept (human)"], ["skipped_current", "already current"], ["rejected", "rejected"], ["legal_needs_review", "need review"]];
const TONE = { running: "brand", done: "success", failed: "error" };
const LABEL = { running: "Running", done: "Finished", failed: "Failed" };

// Owner-only: runs the AI first-draft job for missing/outdated translations and shows its progress.
// The server never overwrites a version a person saved.
export default function TranslateJob({ onDone }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const running = job?.status === "running";

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      try {
        const j = await api(`/admin/i18n/translate/${job.job_id}`);
        setJob((cur) => ({ ...cur, ...j }));
        if (j.status !== "running") {
          if (j.status === "done") toast.success(tr("Translation run finished.")); else toast.error(tr("Translation run failed."));
          onDone();
        }
      } catch (e) { toast.error(e.message); }
    }, 2000);
    return () => clearInterval(t);
  }, [running, job?.job_id, onDone]);

  const start = async () => {
    setBusy(true);
    try { setJob(await api("/admin/i18n/translate", { body: {} })); toast.success(tr("Translation run started.")); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Panel className="flex flex-col gap-3 p-5 md:flex-row md:items-center" data-testid="translate-job">
      <div className="min-w-0 flex-1">
        <p className="cw-soft text-sm">{"AI drafts fill only what's missing or outdated. They never replace a person's save."}</p>
        {job && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="translate-job-progress">
            <Pill tone={TONE[job.status] || "neutral"} data-testid="translate-job-status">{LABEL[job.status] || job.status}</Pill>
            {Object.entries(job.counts || {}).map(([lang, c]) => (
              <span key={lang} className="cw-soft" data-testid={`translate-job-counts-${lang}`}>
                <span className="font-semibold" data-no-i18n>{lang.toUpperCase()}</span>{" "}
                {COUNTS.filter(([k]) => c?.[k]).map(([k, l]) => <span key={k} className="mr-2 tabular-nums">{c[k]} <span>{l}</span></span>)}
              </span>
            ))}
          </div>
        )}
      </div>
      <Button onClick={start} disabled={busy || running} className="shrink-0" data-testid="translate-missing-button">
        <Sparkle size={16} aria-hidden />{running ? "Translating…" : "Translate missing (AI draft)"}
      </Button>
    </Panel>
  );
}
